import react from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Button, Icon, StsIconName, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import colors from '@packages/core/styles/colors';

const useStyles = createUseStyles({
    banner: {
        backgroundColor: colors.blueOne,
        borderRadius: 8,
    },
    bannerLayout: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
    },
    bannerContent: {
        display: 'flex',
        alignItems: 'flex-start',
        flex: 1,
        minWidth: 0,
    },
    textContainer: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    tooltipTrigger: {
        cursor: 'pointer',
        display: 'inline-block',
        maxWidth: '100%',
    },
    titleClamp: {
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word',
        '@media (min-width: 1024px)': {
            overflow: 'visible',
            display: 'block',
            WebkitLineClamp: 'unset',
        },
    },
    descriptionClamp: {
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word',
        '@media (min-width: 1024px)': {
            overflow: 'visible',
            display: 'block',
            WebkitLineClamp: 'unset',
        },
    },
    buttonWrapper: {
        flexShrink: 0,
        alignSelf: 'center',
    },
    tooltip: {
        '& .tooltip-inner': {
            backgroundColor: colors.graySeven,
            borderRadius: 4,
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: '18px',
            maxWidth: 420,
            padding: '8px 12px',
            textAlign: 'left',
        },
    },
});

const styles = StyleSheet.create({
    titleText: {
        color: colors.white,
    },
    descriptionText: {
        color: colors.white,
    },
});

export interface DashboardBannerProps {
    buttonText?: string;
    description: string;
    iconName?: StsIconName;
    onPress?: () => void;
    showButton?: boolean;
    title: string;
}

export function DashboardBanner({
    buttonText = 'Read More',
    description,
    iconName,
    onPress,
    showButton,
    title,
}: DashboardBannerProps) {
    const classes = useStyles();

    return (
        <div className={`${classes.banner} ${classes.bannerLayout} p-6`}>
            <div className={`${classes.bannerContent} mr-4`}>
                {iconName && (
                    <div className="pr-2">
                        <Icon name={iconName} color="white" data-testid="dashboard-banner-icon" />
                    </div>
                )}
                <div className={`${classes.textContainer} ml-3`}>
                    <div className="mb-1">
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id="dashboard-banner-title-tooltip" className={classes.tooltip}>
                                    {title}
                                </Tooltip>
                            }
                        >
                            <span className={`${classes.tooltipTrigger} ${classes.titleClamp}`}>
                                <Typography variant="label" style={styles.titleText}>
                                    {title}
                                </Typography>
                            </span>
                        </OverlayTrigger>
                    </div>
                    <div>
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id="dashboard-banner-description-tooltip" className={classes.tooltip}>
                                    {description}
                                </Tooltip>
                            }
                        >
                            <span className={`${classes.tooltipTrigger} ${classes.descriptionClamp}`}>
                                <Typography style={styles.descriptionText}>{description}</Typography>
                            </span>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>
            {showButton && (
                <div className={classes.buttonWrapper}>
                    <Button
                        iconRight={<Icon size={16} name="chevron-right" color="blueOne" />}
                        onPress={onPress}
                        variant="info"
                    >
                        {buttonText}
                    </Button>
                </div>
            )}
        </div>
    );
}
